'use client';

import type { New } from '@/types';

import { Share2, ThumbsUp } from 'react-feather';
import parse from 'html-react-parser';
import { usePathname } from 'next/navigation';

import {
  Avatar,
  Box,
  Divider,
  Icon,
  IconButton,
  Image,
  Text,
} from '@/components';
import { useShare, useTranslation } from '@/hooks';
import BookMark from '@/svgs/bookmark book.svg';
import { fonts } from '@/theme';
import { jalaliDayjs } from '@/utils';

const BlogContent: React.FC<{ blog: New }> = ({ blog }) => {
  const { t } = useTranslation('pages');

  const pathname = usePathname();
  const handleShare = useShare();
  return (
    <>
      <Box
        display="flex"
        flexDirection={{ base: 'column', xl: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        flexBasis="100%"
        marginBottom="16px"
      >
        <Box
          display="flex"
          alignItems="center"
          width={{ base: '100%', xl: 'max-content' }}
          gap={{ base: 2, md: 5 }}
        >
          {blog.author.current_image ? (
            <Box
              position="relative"
              height="78px"
              aspectRatio={648 / 848}
              sx={{
                '& img': {
                  objectFit: 'cover',
                },
              }}
            >
              <Image src={blog.author.current_image} alt="writer" fill />
            </Box>
          ) : (
            <Avatar
              flexShrink={0}
              bgGradient={'linear(180deg, #8991E0 0%, #E26FCA 100%)'}
              width="52px"
              height="70px"
              paddingTop="-30px"
              overflow="visible"
            />
          )}
          <Text
            fontSize={{ base: 'xs', lg: 'sm' }}
            color="gray.800"
          >{`${t('blog.writer')}: ${blog.author.first_name} ${blog.author.last_name}`}</Text>
          <Divider
            orientation="vertical"
            height="20px"
            borderColor="primary.500"
          />
          <Text
            fontSize={{ base: 'xs', lg: 'sm' }}
            color="gray.800"
          >{`${t('blog.dateOfRelease')}: ${jalaliDayjs(new Date(blog.created_at)).format('YYYY/MM/DD')}`}</Text>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width={{ base: '100%', xl: 'max-content' }}
          marginTop={{ base: '32px', xl: '0' }}
          gap={5}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <Icon as={ThumbsUp} width="21px" height="21px" />
            <Text
              fontSize={{ base: 'xs', lg: 'sm' }}
              color="gray.700"
              marginBottom="-6px"
            >
              {`${blog.rate}/5`}
            </Text>
          </Box>
          <Box display="flex" alignItems="center" gap={3}>
            <Icon as={BookMark} width="21px" height="21px" />
            <Text
              display="flex"
              alignItems="center"
              fontSize={{ base: 'xs', lg: 'sm' }}
              color="gray.700"
            >{`${t('blog.studyTime')}: ${blog.study_time} ${t('blog.min')}`}</Text>
          </Box>
          <IconButton
            aria-label="share"
            variant="ghost"
            colorScheme="text"
            icon={<Icon as={Share2} width="24px" height="26px" />}
            onClick={() => {
              void handleShare({
                title: blog.title,
                url: `${process.env.NEXT_PUBLIC_CLIENT_URL}${pathname}`,
              });
            }}
          />
        </Box>
      </Box>
      <Divider color="gray.700" width="100%" marginBottom="60px" />
      <Box
        sx={{
          '*': { fontFamily: `${fonts.style.fontFamily} !important` },
          table: {
            width: '100% !important',
          },
          'table tbody tr td': {
            textWrap: 'unset',
          },
        }}
      >
        {parse(blog.content)}
      </Box>
    </>
  );
};
export default BlogContent;
